<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="close">
    <div class="modal-container sync-modal">
      <div class="modal-header">
        <h2>🔄 Verificación de Sincronización</h2>
        <button class="close-btn" @click="close">✕</button>
      </div>

      <div class="modal-body">
        <div v-if="isSyncing" class="text-center">
          <div class="spinner"></div>
          <p>Verificando sincronización con el servidor...</p>
        </div>

        <div v-else-if="syncResults.length === 0" class="empty-state">
          <p>No hay resultados de sincronización</p>
        </div>

        <div v-else class="sync-results">
          <!-- Resumen -->
          <div class="sync-summary">
            <div class="summary-card" :class="hasConflicts ? 'warning' : 'success'">
              <h3 v-if="hasConflicts">⚠️ Se encontraron diferencias</h3>
              <h3 v-else>✅ Todo está sincronizado</h3>
              <p>
                {{ filesWithDifferences }} de {{ syncResults.length }} archivos tienen diferencias
              </p>
            </div>
          </div>

          <!-- Lista de archivos -->
          <div class="files-list">
            <div 
              v-for="result in sortedSyncResults" 
              :key="result.key"
              class="file-result"
              :class="result.status"
            >
              <div class="file-header" @click="toggleExpanded(result.key)">
                <div class="file-info">
                  <span class="file-icon">
                    <span v-if="result.status === 'synced'">✅</span>
                    <span v-else-if="result.status === 'different'">⚠️</span>
                    <span v-else>❌</span>
                  </span>
                  <span class="file-name">{{ result.friendlyName }}</span>
                  <span class="file-message">{{ result.message }}</span>
                </div>
                <div class="file-actions">
                  <button 
                    v-if="result.status === 'different'"
                    class="btn btn-sm btn-success"
                    @click.stop="acceptServerVersion(result)"
                    title="Aceptar todos los cambios de este archivo"
                  >
                    ✓ Aceptar todo del archivo
                  </button>
                  <button 
                    v-if="result.status === 'different'"
                    class="btn btn-sm btn-info"
                    @click.stop="viewDiff(result)"
                  >
                    👁️ Ver detalles
                  </button>
                  <span class="expand-icon" v-if="result.differences.length > 0">
                    {{ expandedFiles.has(result.key) ? '▼' : '▶' }}
                  </span>
                </div>
              </div>

              <!-- Diferencias expandidas -->
              <div v-if="expandedFiles.has(result.key) && result.differences.length > 0" class="differences-list">
                <div class="differences-header">
                  <div class="differences-header-content">
                    <strong>Diferencias encontradas:</strong>
                    <div class="header-actions">
                      <button 
                        v-if="!areAllChangesSelectedInFile(result.key, result.differences.length)"
                        class="btn btn-xs btn-outline"
                        @click="selectAllChangesInFile(result.key, result.differences.length)"
                      >
                        ☑️ Seleccionar todas
                      </button>
                      <button 
                        v-else
                        class="btn btn-xs btn-outline"
                        @click="deselectAllChangesInFile(result.key)"
                      >
                        ☐ Deseleccionar todas
                      </button>
                    </div>
                  </div>
                </div>
                <div 
                  v-for="({ diff, originalIndex }) in sortDifferencesByType(result.differences)" 
                  :key="`${result.key}-${diff.id || diff.description || originalIndex}`"
                  class="diff-item"
                  :class="[diff.type, { 'selected': isChangeSelected(result.key, originalIndex) }]"
                >
                  <div class="diff-header-row">
                    <div class="diff-header-left">
                      <input 
                        type="checkbox"
                        class="change-checkbox"
                        :checked="isChangeSelected(result.key, originalIndex)"
                        @change="toggleChangeSelection(result.key, originalIndex)"
                      />
                      <div class="diff-type-badge" :class="diff.type">
                        <span v-if="diff.type === 'added'">➕ Añadido</span>
                        <span v-else-if="diff.type === 'removed'">➖ Eliminado</span>
                        <span v-else-if="diff.type === 'modified'">📝 Modificado</span>
                      </div>
                    </div>
                  </div>
                  <div class="diff-content">
                    <div class="diff-description">
                      {{ diff.description }}
                    </div>
                    
                    <!-- Mostrar valores para modificaciones -->
                    <div v-if="diff.type === 'modified'" class="diff-values">
                      <div class="diff-value server">
                        <div class="diff-value-label">☁️ Valor Servidor:</div>
                        <div class="diff-value-content">{{ formatValue(diff.serverValue || diff.newValue || diff.value || diff.server) }}</div>
                      </div>
                      <div class="diff-arrow">→</div>
                      <div class="diff-value local">
                        <div class="diff-value-label">💻 Valor Local:</div>
                        <div class="diff-value-content">{{ formatValue(diff.localValue || diff.oldValue || diff.local) }}</div>
                      </div>
                    </div>
                    
                    <!-- Mostrar valores para añadidos -->
                    <div v-if="diff.type === 'added'" class="diff-values">
                      <div class="diff-value server added-value">
                        <div class="diff-value-label">☁️ Añadido en servidor:</div>
                        <div class="diff-value-content">{{ formatValue(diff.newValue || diff.value || diff.server) }}</div>
                      </div>
                    </div>
                    
                    <!-- Mostrar valores para eliminados -->
                    <div v-if="diff.type === 'removed'" class="diff-values">
                      <div class="diff-value local removed-value">
                        <div class="diff-value-label">💻 Eliminado del servidor:</div>
                        <div class="diff-value-content">{{ formatValue(diff.oldValue || diff.value || diff.local) }}</div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <div class="footer-left">
          <span v-if="totalSelectedChanges > 0" class="selected-count">
            {{ totalSelectedChanges }} cambio(s) seleccionado(s)
          </span>
        </div>
        <div class="footer-right">
          <button 
            v-if="totalSelectedChanges > 0"
            class="btn btn-primary"
            @click="acceptSelectedChanges"
          >
            ✓ Aceptar cambios seleccionados ({{ totalSelectedChanges }})
          </button>
          <button 
            class="btn btn-secondary"
            @click="close"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  isOpen: Boolean,
  syncResults: {
    type: Array,
    default: () => []
  },
  isSyncing: Boolean,
  hasConflicts: Boolean
})

const emit = defineEmits(['close', 'accept-server-version', 'accept-all-server-versions', 'accept-selected-changes'])

const expandedFiles = ref(new Set())
const selectedChanges = ref(new Map()) // Map<fileKey, Set<diffIndex>>

// Limpiar selecciones cuando cambian los resultados de sincronización
watch(() => props.syncResults, () => {
  selectedChanges.value.clear()
  expandedFiles.value.clear()
}, { deep: true })

const filesWithDifferences = computed(() => {
  return props.syncResults.filter(r => r.status === 'different').length
})

const sortedSyncResults = computed(() => {
  return [...props.syncResults].sort((a, b) => {
    const nameA = (a.friendlyName || a.filename || a.key || '').replace(/^[^\p{L}\p{N}]+/gu, '').trim()
    const nameB = (b.friendlyName || b.filename || b.key || '').replace(/^[^\p{L}\p{N}]+/gu, '').trim()
    return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' })
  })
})

const totalSelectedChanges = computed(() => {
  let count = 0
  selectedChanges.value.forEach(set => {
    count += set.size
  })
  return count
})

/**
 * Organiza las diferencias de un resultado por tipo: added, removed, modified
 */
function sortDifferencesByType(differences) {
  if (!differences || differences.length === 0) return []
  
  const indexed = differences.map((diff, originalIndex) => ({ diff, originalIndex }))
  const sorted = []
  const types = ['added', 'removed', 'modified']
  
  // Agrupar por tipo manteniendo el orden original dentro de cada tipo
  for (const type of types) {
    const filtered = indexed.filter(item => item.diff.type === type)
    sorted.push(...filtered)
  }
  
  return sorted
}

function toggleExpanded(key) {
  if (expandedFiles.value.has(key)) {
    expandedFiles.value.delete(key)
  } else {
    expandedFiles.value.add(key)
  }
}

function close() {
  emit('close')
}

function acceptServerVersion(result) {
  emit('accept-server-version', result)
}

function acceptAllServerVersions() {
  emit('accept-all-server-versions')
}

function toggleChangeSelection(fileKey, diffIndex) {
  if (!selectedChanges.value.has(fileKey)) {
    selectedChanges.value.set(fileKey, new Set())
  }
  
  const fileSelections = selectedChanges.value.get(fileKey)
  if (fileSelections.has(diffIndex)) {
    fileSelections.delete(diffIndex)
    if (fileSelections.size === 0) {
      selectedChanges.value.delete(fileKey)
    }
  } else {
    fileSelections.add(diffIndex)
  }
}

function isChangeSelected(fileKey, diffIndex) {
  return selectedChanges.value.has(fileKey) && selectedChanges.value.get(fileKey).has(diffIndex)
}

function selectAllChangesInFile(fileKey, differencesCount) {
  const fileSelections = new Set()
  for (let i = 0; i < differencesCount; i++) {
    fileSelections.add(i)
  }
  selectedChanges.value.set(fileKey, fileSelections)
}

function deselectAllChangesInFile(fileKey) {
  selectedChanges.value.delete(fileKey)
}

function areAllChangesSelectedInFile(fileKey, differencesCount) {
  if (!selectedChanges.value.has(fileKey)) return false
  const fileSelections = selectedChanges.value.get(fileKey)
  return fileSelections.size === differencesCount
}

function acceptSelectedChanges() {
  const changes = []
  selectedChanges.value.forEach((diffIndices, fileKey) => {
    const result = props.syncResults.find(r => r.key === fileKey)
    if (result) {
      diffIndices.forEach(diffIndex => {
        changes.push({
          fileKey,
          difference: result.differences[diffIndex],
          diffIndex,
          fileResult: result
        })
      })
    }
  })
  
  if (changes.length > 0) {
    emit('accept-selected-changes', changes)
    // Limpiar selecciones después de emitir
    selectedChanges.value.clear()
  }
}

function viewDiff(result) {
  // Expandir automáticamente al ver detalles
  expandedFiles.value.add(result.key)
}

function formatValue(value) {
  if (value === null) return '(null)'
  if (value === undefined) return '(sin valor)'
  if (value === '') return '(string vacío)'
  if (typeof value === 'string') return `"${value}" (string)`
  if (typeof value === 'number') return `${value} (number)`
  if (typeof value === 'boolean') return `${value} (boolean)`
  if (typeof value === 'object') {
    try {
      const typeLabel = Array.isArray(value) ? 'array' : 'object'
      return `${JSON.stringify(value, null, 2)} (${typeLabel})`
    } catch (e) {
      return String(value)
    }
  }
  return String(value)
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: fadeIn 0.2s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.sync-modal {
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: slideUp 0.3s;
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(50px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
}

[data-bs-theme="dark"] .modal-container {
  background: #2a2a2a;
  color: #fff;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

[data-bs-theme="dark"] .modal-header {
  border-bottom-color: #444;
  background: #1e1e1e;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

[data-bs-theme="dark"] .modal-header h2 {
  color: #fff;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

[data-bs-theme="dark"] .close-btn {
  color: #aaa;
}

.close-btn:hover {
  background: #e9ecef;
  color: #000;
}

[data-bs-theme="dark"] .close-btn:hover {
  background: #444;
  color: #fff;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.spinner {
  border: 4px solid #e0e0e0;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 20px auto;
}

[data-bs-theme="dark"] .spinner {
  border-color: #444;
  border-top-color: #007bff;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.sync-summary {
  margin-bottom: 24px;
}

.summary-card {
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.summary-card.success {
  background: rgba(46, 160, 67, 0.1);
  border: 2px solid #2ea043;
}

.summary-card.warning {
  background: rgba(255, 193, 7, 0.1);
  border: 2px solid #ffc107;
}

.summary-card h3 {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  color: #333;
}

[data-bs-theme="dark"] .summary-card h3 {
  color: #fff;
}

.summary-card p {
  margin: 0;
  color: #666;
}

[data-bs-theme="dark"] .summary-card p {
  color: #aaa;
}

.files-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.file-result {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s;
  background: white;
}

[data-bs-theme="dark"] .file-result {
  border-color: #444;
  background: #1e1e1e;
}

.file-result.different {
  border-color: #ffc107;
  background: rgba(255, 193, 7, 0.05);
}

.file-result.synced {
  border-color: #2ea043;
  background: rgba(46, 160, 67, 0.05);
}

.file-result.error {
  border-color: #dc3545;
  background: rgba(220, 53, 69, 0.05);
}

.file-header {
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
}

.file-header:hover {
  background: rgba(0, 0, 0, 0.03);
}

[data-bs-theme="dark"] .file-header:hover {
  background: rgba(255, 255, 255, 0.05);
}

.file-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.file-icon {
  font-size: 1.25rem;
}

.file-name {
  font-weight: 600;
  font-size: 1rem;
  color: #333;
}

[data-bs-theme="dark"] .file-name {
  color: #fff;
}

.file-message {
  color: #666;
  font-size: 0.9rem;
}

[data-bs-theme="dark"] .file-message {
  color: #aaa;
}

.file-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.expand-icon {
  font-size: 0.8rem;
  color: #666;
  margin-left: 8px;
}

[data-bs-theme="dark"] .expand-icon {
  color: #aaa;
}

.differences-list {
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
}

[data-bs-theme="dark"] .differences-list {
  border-top-color: #444;
  background: #1a1a1a;
}

.differences-header {
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

[data-bs-theme="dark"] .differences-header {
  border-bottom-color: #444;
  color: #fff;
}

.differences-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.btn-outline {
  background: transparent;
  border: 1px solid #007bff;
  color: #007bff;
}

.btn-outline:hover {
  background: #007bff;
  color: white;
}

[data-bs-theme="dark"] .btn-outline {
  border-color: #4dabf7;
  color: #4dabf7;
}

[data-bs-theme="dark"] .btn-outline:hover {
  background: #4dabf7;
  color: #000;
}

.diff-item {
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 6px;
  border-left: 4px solid #e0e0e0;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

[data-bs-theme="dark"] .diff-item {
  border-left-color: #444;
  background: #2a2a2a;
}

.diff-item.added {
  border-left-color: #2ea043;
  background: rgba(46, 160, 67, 0.05);
}

.diff-item.removed {
  border-left-color: #dc3545;
  background: rgba(220, 53, 69, 0.05);
}

.diff-item.modified {
  border-left-color: #ffc107;
  background: rgba(255, 193, 7, 0.05);
}

.diff-item.selected {
  box-shadow: 0 0 0 2px #007bff;
  background: rgba(0, 123, 255, 0.08) !important;
}

[data-bs-theme="dark"] .diff-item.selected {
  box-shadow: 0 0 0 2px #4dabf7;
  background: rgba(77, 171, 247, 0.15) !important;
}

.diff-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.diff-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.change-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #007bff;
}

.diff-type-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.btn-xs {
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 500;
}

.diff-type-badge.added {
  background: #2ea043;
  color: white;
}

.diff-type-badge.removed {
  background: #dc3545;
  color: white;
}

.diff-type-badge.modified {
  background: #ffc107;
  color: #000;
}

.diff-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
}

.diff-description {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #333;
  font-weight: 600;
}

[data-bs-theme="dark"] .diff-description {
  color: #ddd;
}

.diff-values {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 12px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  margin-top: 8px;
}

[data-bs-theme="dark"] .diff-values {
  background: rgba(255, 255, 255, 0.05);
}

.diff-value {
  flex: 1;
  min-width: 0;
}

.diff-value-label {
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 6px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

[data-bs-theme="dark"] .diff-value-label {
  color: #aaa;
}

.diff-value-content {
  padding: 10px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
  color: #333;
}

[data-bs-theme="dark"] .diff-value-content {
  background: #1a1a1a;
  border-color: #444;
  color: #ddd;
}

.diff-value.local .diff-value-content {
  border-left: 3px solid #dc3545;
}

.diff-value.server .diff-value-content {
  border-left: 3px solid #2ea043;
}

.diff-value.added-value .diff-value-content {
  border-left: 3px solid #2ea043;
  background: rgba(46, 160, 67, 0.05);
}

.diff-value.removed-value .diff-value-content {
  border-left: 3px solid #dc3545;
  background: rgba(220, 53, 69, 0.05);
}

.diff-arrow {
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  color: #666;
  margin: 30px 0 0 0;
}

[data-bs-theme="dark"] .diff-arrow {
  color: #aaa;
}

.diff-more {
  padding: 12px;
  text-align: center;
  color: #666;
  font-style: italic;
}

[data-bs-theme="dark"] .diff-more {
  color: #aaa;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
}

[data-bs-theme="dark"] .modal-footer {
  border-top-color: #444;
  background: #1e1e1e;
}

.footer-left {
  flex: 1;
}

.footer-right {
  display: flex;
  gap: 12px;
}

.selected-count {
  font-weight: 600;
  color: #007bff;
  font-size: 0.95rem;
}

[data-bs-theme="dark"] .selected-count {
  color: #4dabf7;
}

.text-center {
  text-align: center;
  color: #333;
}

[data-bs-theme="dark"] .text-center {
  color: #fff;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #666;
}

[data-bs-theme="dark"] .empty-state {
  color: #aaa;
}
</style>
